// First-touch attribution (R6.10 B1). One object, `buildLandingTouch`, built once per document
// load: the external referrer host, the landing path, the landing URL's utm/ref/gclid, and when.
//
// It is stored on the device only when the visitor has consented (outside the EU/EEA/UK the app
// sets that automatically; inside, only after "Accept") and reaches the sign-up through
// `encodeSignupAttribution`. Everything is client-side and every browser API is guarded: a
// `localStorage` that throws (Safari private mode, quota) means nothing is stored and the sign-up
// still goes on. The stored entry is as untrusted as a hand-crafted URL, so it goes through the
// codec's whitelist and per-key validators on the way in and out.
//
// Importable from client components only in practice (it reads `localStorage`), but nothing here
// throws on the server.

import { type Attribution, decodeAttribution, encodeAttribution } from '@/lib/auth/attribution'
import { normaliseHost } from '@/lib/attribution/traffic-source'
import { publicEnv } from '@/lib/env'
import { nowDate } from '@/lib/dates/now'

export const FIRST_TOUCH_STORAGE_KEY = 'ranksurf:first-touch'

/** The widest admin window (`/admin/signups` 7/28/90): an older touch would outrank a newer visit. */
export const FIRST_TOUCH_MAX_AGE_DAYS = 90

const DAY_MS = 24 * 60 * 60 * 1000

/** Paths a visit never counts as a landing: signed-in, internal, or token-bearing report URLs. */
const EXCLUDED_PREFIXES = [
  '/app',
  '/onboarding',
  '/admin',
  '/api',
  '/auth/complete',
  '/auth/callback',
  '/report/',
  '/design-system',
]

/** The auth round-trip: a page reached back from an OAuth cancel must never read as Referral. */
const AUTH_ROUND_TRIP_HOSTS = ['accounts.google.com']

/** Whether `touch` has a valid `first_touch_at` no older than {@link FIRST_TOUCH_MAX_AGE_DAYS}. */
function isFresh(touch: Attribution, now: Date): boolean {
  const at = touch.first_touch_at ? Date.parse(touch.first_touch_at) : NaN
  return !Number.isNaN(at) && now.getTime() - at <= FIRST_TOUCH_MAX_AGE_DAYS * DAY_MS
}

function hostOf(url: string | undefined): string | null {
  if (!url) return null
  try {
    return normaliseHost(new URL(url).hostname)
  } catch {
    return null
  }
}

/**
 * The hosts a referrer is "us" for: our own site, localhost, and the auth round trip (Google's
 * consent screen, the Supabase project host). The current page's own hostname is added by the
 * caller (it knows `window.location`).
 */
export function defaultOwnHosts(): string[] {
  return [
    hostOf(publicEnv.appUrl),
    hostOf(publicEnv.supabaseUrl),
    'localhost',
    '127.0.0.1',
    '[::1]',
    ...AUTH_ROUND_TRIP_HOSTS,
  ].filter((host): host is string => typeof host === 'string' && host.length > 0)
}

function isExcludedPath(pathname: string): boolean {
  return EXCLUDED_PREFIXES.some((prefix) =>
    prefix.endsWith('/')
      ? pathname.startsWith(prefix)
      : pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

const LANDING_PARAM_KEYS = [
  'ref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
] as const

/**
 * A document view's first touch, or `null` when the view is not a landing: the referrer is us or
 * the auth round trip (internal navigation), the path is excluded, or the href is unparseable.
 * Pure. Host lower-cased with `www.` stripped; path only (no query, no hash); `landing_path` is
 * scrubbed of any invite/claim token by the codec before it can be stored or sent.
 */
export function buildLandingTouch(args: {
  href: string
  referrer: string
  now: Date
  ownHosts: readonly string[]
}): Attribution | null {
  let url: URL
  try {
    url = new URL(args.href)
  } catch {
    return null
  }
  if (isExcludedPath(url.pathname)) return null

  const own = new Set(args.ownHosts.map(normaliseHost))
  own.add(normaliseHost(url.hostname))
  const referrerHost = hostOf(args.referrer)
  if (referrerHost !== null && own.has(referrerHost)) return null

  const raw: Attribution = {
    ...(referrerHost !== null ? { referrer_host: referrerHost } : {}),
    landing_path: url.pathname,
    first_touch_at: args.now.toISOString(),
  }
  for (const key of LANDING_PARAM_KEYS) {
    const value = url.searchParams.get(key)
    if (value) raw[key] = value
  }
  // The codec is the one validator: it drops (never coerces) anything it does not accept, and
  // scrubs a token out of `landing_path`.
  const clean = decodeAttribution(encodeAttribution(raw))
  return clean?.landing_path ? clean : null
}

// -- The in-memory landing view ----------------------------------------------------------------
// The touch of this document's first view, held in module memory (not a ref, so React strict
// mode's double effect captures once). It is what resolves a sign-up when nothing is stored: a
// visitor who landed on `/auth/login` itself (an email link, a direct visit) and has not consented.

interface LandingView {
  captured: boolean
  touch: Attribution | null
  pathname: string
  left: boolean
}

let landing: LandingView = { captured: false, touch: null, pathname: '', left: false }

/** Runs `build` on the first call of this document only. */
export function captureLandingOnce(
  build: () => { touch: Attribution | null; pathname: string }
): void {
  if (landing.captured) return
  const { touch, pathname } = build()
  landing = { captured: true, touch, pathname, left: false }
}

/** Every pathname change after the landing marks the landing view as left. */
export function noteViewPath(pathname: string): void {
  if (landing.captured && pathname !== landing.pathname) landing.left = true
}

/** The in-memory touch of this document's first view; only until the visitor navigates away. */
export function landingViewTouch(): Attribution | null {
  return landing.captured && !landing.left ? landing.touch : null
}

/** The touch built for this document's first view (null when it was not a landing). */
export function getLandingTouch(): Attribution | null {
  return landing.captured ? landing.touch : null
}

// -- The stored entry --------------------------------------------------------------------------

type TouchStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function storageOrNull(): TouchStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** Removes the stored entry. Never throws. */
export function clearStoredTouch(): void {
  try {
    storageOrNull()?.removeItem(FIRST_TOUCH_STORAGE_KEY)
  } catch {
    // nothing to do: an entry we cannot remove we also could not have written
  }
}

/**
 * The stored first touch, or `null`. An entry that fails to parse or validate, has no
 * `first_touch_at`, or is older than {@link FIRST_TOUCH_MAX_AGE_DAYS} is cleared and ignored.
 */
export function readStoredTouch(now: Date = nowDate()): Attribution | null {
  let raw: string | null
  try {
    raw = storageOrNull()?.getItem(FIRST_TOUCH_STORAGE_KEY) ?? null
  } catch {
    return null
  }
  if (raw === null) return null

  const touch = decodeAttribution(raw)
  if (!touch || !isFresh(touch, now)) {
    clearStoredTouch()
    return null
  }
  return touch
}

/** Stores `touch` unless a valid entry already exists. Returns whether it wrote one. */
export function storeTouchIfAbsent(touch: Attribution, now: Date = nowDate()): boolean {
  if (readStoredTouch(now) !== null) return false
  const encoded = encodeAttribution(touch)
  if (!encoded) return false
  try {
    const storage = storageOrNull()
    if (!storage) return false
    storage.setItem(FIRST_TOUCH_STORAGE_KEY, encoded)
    return true
  } catch {
    return false
  }
}

// -- The carried entry -------------------------------------------------------------------------
// An EU/EEA/UK visitor who has not accepted has nothing stored on their device. Their landing touch
// still reaches the sign-up when they click a sign-up link on the page they landed on: the link's
// own address carries it as `ft=`, the same codec as everywhere else.

const SIGNUP_PATH = '/auth/login'
const CARRIED_PARAM = 'ft'

/**
 * `href` with the landing `touch` appended as `ft=` (an existing `ft` is replaced), as a
 * path-and-query URL; or `null` when `href` is not the same-origin sign-up page, is unparseable,
 * or the touch encodes to nothing. Pure.
 */
export function decorateSignupHref(href: string, touch: Attribution, base: string): string | null {
  let url: URL
  let origin: string
  try {
    url = new URL(href, base)
    origin = new URL(base).origin
  } catch {
    return null
  }
  if (url.origin !== origin || url.pathname !== SIGNUP_PATH) return null
  const encoded = encodeAttribution(touch)
  if (!encoded) return null
  url.searchParams.set(CARRIED_PARAM, encoded)
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * The touch a sign-up link carried in `?ft=`, or `null`. Only what the codec validates survives,
 * a touch without a `landing_path` is not a touch, and one with no valid `first_touch_at` or older
 * than {@link FIRST_TOUCH_MAX_AGE_DAYS} (a stale bookmarked link) is ignored. Never throws.
 */
export function readCarriedTouch(search: string, now: Date = nowDate()): Attribution | null {
  let encoded: string | null
  try {
    encoded = new URLSearchParams(search).get(CARRIED_PARAM)
  } catch {
    return null
  }
  const touch = encoded ? decodeAttribution(encoded) : null
  if (!touch?.landing_path || !isFresh(touch, now)) return null
  return touch
}

function currentSearch(): string {
  try {
    return typeof window === 'undefined' ? '' : window.location.search
  } catch {
    return ''
  }
}

/**
 * The first touch a sign-up should carry: the stored entry; else the in-memory touch when the
 * current view is still the landing view AND it has an external referrer (a measured arrival
 * outranks a shared or forged `ft=` link); else the touch the sign-up link carried (`ft=`, an
 * in-site click from our own landing page); else the in-memory landing touch without a referrer
 * (`/auth/login` itself was a direct landing); else `null`.
 */
export function resolveFirstTouch(now: Date = nowDate()): Attribution | null {
  const stored = readStoredTouch(now)
  if (stored) return stored
  const view = landingViewTouch()
  if (view?.referrer_host) return view
  return readCarriedTouch(currentSearch(), now) ?? view
}

/** The login-URL keys that describe the sign-up click itself, not where the visitor came from. */
const URL_CLICK_KEYS = ['ref', 'mode', 'domain'] as const

/**
 * The `attribution` string a sign-up carries. With a first touch: the touch's keys win for every
 * key it carries and the login URL contributes ONLY `ref`, `mode`, `domain` (the CTA's
 * `ref=header`) — its `utm_*`/`gclid` are a last touch and never mix into a first touch. With no
 * first touch the URL attribution is carried as it is. Does not read `document.referrer`: an unconsented visitor who browsed before
 * signing up reads "Not captured", exactly as promised. Call at submit time only.
 */
export function encodeSignupAttribution(urlAttribution: Attribution | null): string {
  const touch = resolveFirstTouch()
  if (!touch) return encodeAttribution(urlAttribution ?? {})
  const click: Attribution = {}
  for (const key of URL_CLICK_KEYS) {
    const value = urlAttribution?.[key]
    if (value) click[key] = value
  }
  return encodeAttribution({ ...click, ...touch })
}
